/**
 * Character-level comparison of two short values, for names. Pure.
 *
 * `diff.ts` deliberately stops at whole lines, because two independent
 * readings of free text differ in wording all the time. A name is the
 * exception: it is one short string that should be spelt the same on both
 * sides, and the question a pharmacist has on a diverging name is which
 * letters are off -- "Souza" against "Sousa" is hard to spot in two bold
 * red lines.
 *
 * Case and accents are not differences here, for the same reason the
 * name check folds them (`normalizeTerm` in @rp/core): the ERP keys names in
 * capitals without accents, and painting every letter of "JOSÉ" against
 * "José" would bury the one letter that is actually wrong.
 */

export interface CharSegment {
  readonly text: string
  readonly differs: boolean
}

const fold = (char: string) => char.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()

/**
 * Both sides cut into runs that match the other side and runs that do not,
 * by longest common subsequence. Names are a few dozen characters, so the
 * quadratic table costs nothing.
 */
export function diffCharacters(
  prescription: string,
  system: string,
): { prescription: CharSegment[]; system: CharSegment[] } {
  const left = [...prescription]
  const right = [...system]
  const a = left.map(fold)
  const b = right.map(fold)

  // lengths[i][j]: the longest common subsequence of a[i..] and b[j..].
  const lengths = Array.from({ length: a.length + 1 }, () =>
    Array.from({ length: b.length + 1 }, () => 0),
  )
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      lengths[i]![j] =
        a[i] === b[j]
          ? lengths[i + 1]![j + 1]! + 1
          : Math.max(lengths[i + 1]![j]!, lengths[i]![j + 1]!)
    }
  }

  const leftKept = Array.from({ length: a.length }, () => false)
  const rightKept = Array.from({ length: b.length }, () => false)
  let i = 0
  let j = 0
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      leftKept[i] = true
      rightKept[j] = true
      i += 1
      j += 1
    } else if (lengths[i + 1]![j]! >= lengths[i]![j + 1]!) {
      i += 1
    } else {
      j += 1
    }
  }

  return { prescription: segments(left, leftKept), system: segments(right, rightKept) }
}

function segments(chars: readonly string[], kept: readonly boolean[]): CharSegment[] {
  const out: CharSegment[] = []
  chars.forEach((char, index) => {
    const differs = !kept[index]
    const last = out[out.length - 1]
    if (last !== undefined && last.differs === differs) {
      out[out.length - 1] = { text: last.text + char, differs }
    } else {
      out.push({ text: char, differs })
    }
  })
  return out
}
